import React from "react"

import debounce from "lodash/debounce"
import { IoMdSearch } from "react-icons/io"

import { useIsInViewport } from "../../utils/useIsInViewport"

export type DropdownItemProps = {
  onClick: () => void
  label1: string
  label2?: string
  isChecked?: boolean
  showCheck?: boolean
  className?: string
  isBase?: boolean
}

export const BasicDropdownItem: React.FC<DropdownItemProps> = ({
  onClick,
  label1,
  label2,
  isChecked = false,
  showCheck = true,
  className,
  isBase = false,
}) => {
  const ref = React.useRef(null)
  const inView = useIsInViewport(ref)
  if (!inView) {
    return (
      <div ref={ref} data-role="dropdown-item" className={className}>
        <div>
          <p>Loading...</p>
        </div>
      </div>
    )
  }

  const baseWrapperClasses = isBase ? `bg-green-50 border border-green-200` : ``
  const combinedWrapperClass = [className, baseWrapperClasses]
    .filter(Boolean)
    .join(` `)

  return (
    <div
      onClick={() => {
        onClick()
      }}
      ref={ref}
      data-role="dropdown-item"
      className={combinedWrapperClass}
    >
      <div>
        <p>
          {label1}
          {isBase && (
            <span className="base-chip ml-2 rounded-full border border-green-500 bg-white px-2 py-[2px] text-xs leading-tight font-medium text-green-600 uppercase">
              BASE
            </span>
          )}
        </p>
        <span style={{ display: `none` }}>{label2}</span>
      </div>
      {showCheck ? <input type="radio" readOnly checked={isChecked} /> : null}
    </div>
  )
}

export const SearchBar: React.FC<{
  setDebouncedInput: React.Dispatch<React.SetStateAction<string>>
  optionsLength: number
  onSearchChange?: (value: string) => void
  alwaysShowSearch?: boolean
}> = ({
  setDebouncedInput,
  optionsLength,
  alwaysShowSearch,
  onSearchChange,
}) => {
  const [input, setInput] = React.useState<string>(``)
  const inputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    inputRef.current?.focus({ preventScroll: true })
  }, [optionsLength])

  const debouncedHandleChange = React.useCallback(
    debounce((value) => {
      onSearchChange?.(value)
      setDebouncedInput(value)
    }, 300),
    [],
  )

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setInput(value)
    debouncedHandleChange(value)
  }

  return (
    <div
      onClick={(e) => {
        e.stopPropagation()
        inputRef.current?.focus({ preventScroll: true })
      }}
      data-role="dropdown-search"
      style={{
        display: alwaysShowSearch || optionsLength >= 10 ? `flex` : `none`,
      }}
    >
      <IoMdSearch />
      <input
        className="outline-none"
        ref={inputRef}
        type="text"
        onClick={(e) => e.stopPropagation()}
        onChange={handleChange}
        placeholder="Search..."
        value={input}
      />
    </div>
  )
}
